import { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/constants";
import { useLangue } from "@/lib/i18n";
import { useAuth } from "@/lib/hooks/useAuth";
import { startOrderAlarm, stopOrderAlarm } from "@/lib/utils/order-alarm";

/**
 * ===========================================================================
 *  CE QUE LE MAGASIN PREPARE LUI-MEME
 * ===========================================================================
 *
 *  Legumes prepares, legumes decoupes, recettes decoupees, salade d'herbes
 *  prete a l'emploi : ces quatre rayons ne sont pas mis en sachet par le
 *  fournisseur, mais par le responsable commande. Jusqu'ici seul le
 *  fournisseur etait prevenu — le responsable decouvrait le travail quand
 *  le livreur etait deja la.
 *
 *  Cette alarme sonne DE PLUS, pas A LA PLACE : le fournisseur garde la
 *  sienne. Les deux doivent etre prevenus, parce que les deux ont un geste
 *  a faire.
 *
 *  Le timbre est celui de la sonnette (« du travail arrive »), pas celui
 *  de ses commandes habituelles : le responsable doit entendre a l'oreille
 *  que celle-ci lui demande un geste de preparation, pas seulement une
 *  commande de plus.
 *
 *  Comme l'alarme du fournisseur, elle SUIT L'ETAT : tant qu'un panier de
 *  ces rayons est en attente, ca sonne. Personne ne coupe le son — il
 *  s'arrete quand la preparation commence.
 * ===========================================================================
 */

type PanierAPreparer = {
  id: string;
  code: string;
  categories: string[];
};

export default function AlarmePreparationMagasin({
  storeId,
}: {
  /** Le magasin du responsable. Sans lui, on n'ecoute rien. */
  storeId: string | null;
}) {
  const { t } = useLangue();
  // Deux responsables (15/10/2026) : ne sonne que chez celui a qui la
  // commande est donnee — ou chez tous si elle n'est a personne.
  const { user } = useAuth();
  const moi = user?.uid || "";
  const [paniers, setPaniers] = useState<PanierAPreparer[]>([]);

  /**
   * LES PANIERS DEJA VUS.
   *
   * Le bandeau ne peut pas attendre un changement d'etat pour disparaitre :
   * le responsable ne « commence » pas le panier d'un fournisseur, il
   * prepare a cote. Sans ce bouton, le bandeau restait affiche et le son
   * continuait — et « Voir » ne faisait rien, puisqu'il etait deja sur
   * cette page.
   *
   * La liste est gardee dans l'onglet (sessionStorage) : recharger la page
   * ne doit pas refaire sonner ce qu'il a deja vu. Elle disparait a la
   * fermeture de l'onglet, donc rien ne se perd durablement.
   */
  const [vus, setVus] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem("preparationVue") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!storeId) return;

    // Deux egalites, sans tri : Firestore sert ce genre de requete avec les
    // index a champ unique qui existent deja. Ajouter un `orderBy`
    // exigerait un index composite, et l'alarme resterait muette tant qu'il
    // ne serait pas cree — exactement ce qu'on ne peut pas se permettre
    // ici.
    const q = query(
      collection(db, COLLECTIONS.SUPPLIER_BASKETS),
      where("aPreparerParMagasin", "==", true),
      where("status", "==", "PENDING"),
    );

    const arret = onSnapshot(
      q,
      (snapshot) => {
        // Le magasin est filtre en memoire : une troisieme egalite ne
        // couterait rien a Firestore, mais les anciens paniers n'ont pas
        // encore de `storeId` et disparaitraient de l'alarme.
        const miens = snapshot.docs
          .map((doc: any) => {
            const d: any = doc.data() || {};
            return {
              id: doc.id,
              storeId: d.storeId ? String(d.storeId) : null,
              responsableId: d.responsableId ? String(d.responsableId) : null,
              code: String(d.orderCode || d.orderId || doc.id).slice(0, 20),
              categories: Array.isArray(d.categoriesAPreparer)
                ? d.categoriesAPreparer.map(String)
                : [],
            };
          })
          .filter((p) => !p.storeId || p.storeId === storeId)
          .filter((p) => !p.responsableId || !moi || p.responsableId === moi)
          .filter((p) => !vus.includes(p.id));

        if (miens.length === 0) {
          stopOrderAlarm();
          setPaniers([]);
          return;
        }

        startOrderAlarm("fournisseur");
        setPaniers(miens);
      },
      (err) => {
        // Une ecoute interrompue ne doit jamais casser la page.
        console.error("[ALARME PREPARATION] Ecoute interrompue :", err?.message);
      },
    );

    return () => {
      arret();
      stopOrderAlarm();
    };
  }, [storeId, vus, moi]);

  if (paniers.length === 0) return null;

  // Les rayons concernes, sans doublon : le responsable doit savoir ce
  // qu'il y a a preparer avant meme d'ouvrir la commande.
  const rayons = [...new Set(paniers.flatMap((p) => p.categories))];

  return (
    <div className="fixed inset-x-0 top-0 z-[10000] px-3 pt-3">
      <div className="mx-auto max-w-3xl rounded-2xl border-2 border-orange-500 bg-orange-600 text-white shadow-2xl">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-lg font-extrabold leading-tight">
              {paniers.length === 1
                ? t("À PRÉPARER EN MAGASIN")
                : `${paniers.length} ${t("À PRÉPARER EN MAGASIN")}`}
            </p>
            {rayons.length > 0 && (
              <p className="mt-0.5 truncate text-sm text-orange-100">
                {rayons.join("  ·  ")}
              </p>
            )}
            <p className="mt-0.5 truncate text-xs text-orange-200">
              {paniers
                .slice(-3)
                .map((p) => p.code)
                .join("  ·  ")}
            </p>
          </div>

          {/* « J'ai vu » plutot que « Voir » : le responsable est deja sur
              cette page, il n'y a nulle part ou aller. Le seul geste utile
              est de dire qu'il a lu — et c'est ce qui arrete le son. */}
          <button
            type="button"
            onClick={() => {
              const ids = [...vus, ...paniers.map((p) => p.id)];
              try {
                sessionStorage.setItem("preparationVue", JSON.stringify(ids));
              } catch {
                /* Navigation privee : le bandeau se taira jusqu'au
                   prochain rechargement, ce qui suffit. */
              }
              stopOrderAlarm();
              setVus(ids);
              setPaniers([]);
            }}
            className="shrink-0 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-orange-700 hover:bg-orange-50"
          >
            {t("J'ai vu")}
          </button>
        </div>
      </div>
    </div>
  );
}
